<template>
  <component
    v-if="['audio', 'video'].find((e) => e === type)"
    :is="['audio', 'video'].find((e) => e === type)"
    ref="playerRef"
    controls
    crossorigin
    class="w-full"
  >
    <source :src="source" type="application/x-mpegURL">
    <track
      v-for="cap in enabledCaptions"
      kind="captions"
      :key="cap.srclang"
      :label="cap.label"
      :srclang="cap.srclang"
      :src="cap.src"
    />
  </component>
  <div v-else class="plyr__video-embed" ref="playerRef">
    <iframe
      :src="source"
      allowfullscreen
      allowtransparency
      allow="autoplay"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Hls from 'hls.js'
// @ts-ignore
import Plyr from 'plyr'
import { useSystemStore } from '@/stores'
// import { languageType } from '@/common/util'

import playerConfig from './playerConfig'

import 'plyr/dist/plyr.css'
import { midiaService } from '@/services/midias.service'

const { t } = useI18n()

const props = defineProps<{
  source?: string
  pullzone?: string
  type: string
  midiaId?: string
  videoId?: string
  trackProgress?: boolean
  progress?: number
  captions?: {
    src: string
    label: string
    srclang: string
  }[]
  chapters?: {
    start: number
    end: number
    title: string
  }[]
  disableFastForward?: boolean
}>()

const emit = defineEmits(['onComplete', 'onProgress'])

let hls: Hls | null = null
let player: Plyr | null = null

let autoChapters = [] as { start: number, title: string }[]
let autoCaptions = ref([] as { src: string, label: string, srclang: string }[])

const enabledCaptions = computed(() => {
  return (!props.captions || props.captions.length === 0) ? autoCaptions.value : props.captions
})

// Armazena o progresso já percorrido pelo vídeo
let watchedTime = 0

const playerRef = ref()

const updateQuality = (newQuality) => {
  if (!hls) return

  hls.levels.forEach((level, levelIndex) => {
    if (hls && level.height === newQuality) {
      hls.currentLevel = levelIndex
    }
  })
}

const initPlyr = (config?: {}) => {
  if (player) player.destroy()

  playerConfig.autoplay = useSystemStore().options.autoplay
  playerConfig.volume = useSystemStore().options.volume

  const controls = ['play-large', 'restart', 'rewind', 'fast-forward', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'captions', 'settings', 'pip', 'airplays', 'fullscreen']
  const speed = {
    selected: 1,
    options: [0.5, 0.75, 1, 1.25, 1.5, 2]
  }

  if (props.disableFastForward) {
    controls.splice(controls.indexOf('fast-forward'), 1)
    speed.options = [0.5, 0.75, 1]
  }

  let captions = {}
  if (enabledCaptions.value.length > 0) {
    captions = {
      update: true,
      active: true
    }
  }

  const points = (props.chapters?.length ? props.chapters : autoChapters).map(el => ({ time: el.start, label: el.title }))

  player = new Plyr(playerRef.value, {
    ...playerConfig,
    ...config,
    controls,
    speed,
    // ratio: '16:9',
    keyboard: {
      global: true
    },
    captions,
    markers: {
      enabled: points.length > 0,
      points
    },
    displayDuration: true
  } as any)

  // Caso o usuário "avance" manualmente
  player.on('seeked', () => {
    if (player && props.disableFastForward && player.currentTime > watchedTime) {
      player.currentTime = watchedTime
    }
  })

  if (!props.trackProgress) return
  player.on('timeupdate', onTimeUpdate)
  player.on('ended', onEnded)

  if (['audio', 'video'].includes(props.type)) player.on('loadedmetadata', setCurrentTime)
  else player.on('ready', checkDurationRecursive)

  return player
}

let watchedTM : ReturnType<typeof setTimeout> | null = null
const progressTrack = {
  blocked: false,
  value: 0,
  timeout: null as ReturnType<typeof setTimeout> | null
}

const updateProgress = async (value: number, force?: boolean) => {
  if (!force) {
    if (progressTrack.blocked) return

    setTimeout(() => {
      progressTrack.blocked = false
    }, 10000)

    progressTrack.blocked = true
    progressTrack.value = value
  }

  emit('onProgress', value)
}

const onTimeUpdate = () => {
  if (watchedTM) clearTimeout(watchedTM)

  watchedTM = setTimeout(() => {
    if (player && player.playing && watchedTime < player.currentTime) {
      watchedTime = player.currentTime
    }
  }, 100)

  let percent = player?.currentTime && player?.duration ? ((player.currentTime * 100) / player.duration) : 0

  if ((Number(player?.currentTime) >= (Number(player?.duration) - 5)) && progressTrack?.value !== 100 && props.type === 'video') {
    progressTrack.blocked = false
    percent = 100
    emit('onComplete')
  }

  updateProgress(percent)
}

const onEnded = () => {
  updateProgress(100, true)
  emit('onComplete')
}

const checkDurationRecursive = (event: Plyr.PlyrEvent) => {
  if (player && player.duration === 0) setTimeout(() => {
    return checkDurationRecursive(event)
  }, 50)
  else return setCurrentTime(event)
}

const setCurrentTime = (event: Plyr.PlyrEvent) => {
  if (props.progress && props.progress > 0 && props.progress < 100 && player) {
    player.currentTime = (props.progress / 100) * event.detail.plyr.duration
  }
}

const initHls = async () => {
  if (!Hls.isSupported()) throw t('erros.notSupportStreaming')
  if (hls) hls.destroy()

  if (!props.source)
    return

  const videoInfo = midiaService.getVideoInfo(props.videoId || '')
    .then((res) => {
      if (res && res.data) {
        autoChapters = res.data.chapters.map((chap: any) => ({
          start: chap.start,
          end: chap.end,
          title: chap.title
        }))

        autoCaptions.value = res.data.captions.map((cap: any) => ({
          src: `https://${props.pullzone}/${props.videoId}/captions/${cap.srclang}.vtt`,
          label: cap.srclang,
          srclang: cap.srclang
        }))
      }
    })

  hls = new Hls()
  hls.loadSource(props.source)

  // Promise utilizada para manter o fluxo sincrono
  await Promise.all([
    new Promise((resolve) => {
      if (!hls) return

      // Lê o manifest da playlist m3u8 (Importante para extrari informações para o Plyr)
      hls.on(Hls.Events.MANIFEST_PARSED, async () => {
        const availableQualities = hls?.levels.map((l) => l.height) || []

        playerConfig.quality = {
          default: availableQualities.find((el) => el === 1080) || availableQualities[availableQualities.length - 1],
          options: availableQualities,
          forced: true, // Garante que o Plyr vai chamar o Hls para atualizar o nivel de qualidade
          onChange: (e) => updateQuality(e)
        }

        await videoInfo
        await initPlyr(playerConfig)

        player.on('languagechange', () => {
          setTimeout(() => {
            if (hls && player) hls.subtitleTrack = player.currentTrack
          }, 50)
        })

        resolve(true)
      })

      hls.attachMedia(playerRef.value)
    })
  ])
}

const loadMediaVideo = async () => {
  (Hls.isSupported() && props.type === 'video') ? initHls() : initPlyr()
}

const loadMediaAudio = async () => {
  initPlyr()
  if (!player) return

  player.source = {
    type: 'audio',
    title: '',
    sources: [
      {
        src: props.source || '',
        type: 'audio/mp3'
      }
    ]
  }
}

onMounted(async () => {
  switch (props.type) {
    case 'video':
      await loadMediaVideo()
      break;
    case 'audio':
      await loadMediaAudio()
      break;
    default:
      initPlyr()
      break;
  }
})

onBeforeUnmount(() => {
  try {
    if (player) player.destroy()
    if (hls) hls.destroy()
  } catch (error) {
    console.log('destroy error', error)
  }
})

defineExpose({
  player,
  getDuration: () => player?.duration,
  getCurrentTime: () => player?.currentTime,
  getRemainingTime: () => Number(player?.duration) > 0 ? Number(player?.duration) - Number(player?.currentTime) : -1
})
</script>

<style lang="scss">
:root {
  --plyr-color-main: #875fc1;
}

.plyr__tooltip {
  max-width: none !important;
}

.plyr__menu__container {
  z-index: 10 !important;
}
</style>
